import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, RefreshCw, Send, X } from 'lucide-react';
import axios from 'axios';
import { config } from '../config/index';
import { authService } from '../services/auth';

interface FeedbackButtonProps {
  jobId: string;
  category?: string;
  onRegenerated?: (newResult: any) => void;
}

export const FeedbackButton: React.FC<FeedbackButtonProps> = ({ 
  jobId, 
  category,
  onRegenerated 
}) => {
  const [showModal, setShowModal] = useState(false);
  const [feedbackType, setFeedbackType] = useState<string>('');
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  const feedbackOptions = [
    { value: 'titre_trop_long', label: 'Le titre est trop long' },
    { value: 'titre_pas_accrocheur', label: "Le titre n'est pas assez accrocheur" },
    { value: 'resume_trop_court', label: 'Le résumé manque de détails' },
    { value: 'resume_trop_long', label: 'Le résumé est trop verbeux' },
    { value: 'style_inadequat', label: 'Le style ne convient pas' },
    { value: 'informations_manquantes', label: 'Des informations importantes manquent' },
    { value: 'autre', label: 'Autre problème' }
  ];

  const handlePositiveFeedback = async () => {
    try {
      const token = authService.getToken();
      await axios.post(
        `${config.api.titreEndpoint}/feedback`,
        {
          job_id: jobId,
          feedback_type: 'positive',
          feedback_text: 'Résultat satisfaisant',
          action: 'save_only'
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      // Simple feedback visuel
      alert('Merci pour votre retour positif !');
    } catch (error) {
      console.error('Error submitting positive feedback:', error);
    }
  };

  const handleNegativeFeedback = () => {
    setShowModal(true);
  };

  const submitFeedback = async (action: 'save_only' | 'regenerate') => {
    if (!feedbackType) {
      alert('Veuillez sélectionner un type de problème');
      return;
    }

    if (feedbackType === 'autre' && !feedbackText.trim()) {
      alert('Veuillez décrire le problème');
      return;
    }

    setIsSubmitting(true);
    if (action === 'regenerate') {
      setIsRegenerating(true);
    }

    try {
      const token = authService.getToken();
      const response = await axios.post(
        `${config.api.titreEndpoint}/feedback`,
        {
          job_id: jobId,
          feedback_type: feedbackType,
          feedback_text: feedbackText,
          action: action
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        if (action === 'regenerate') {
          // La régénération est en cours, commencer le polling
          alert('Régénération en cours... Le résultat sera mis à jour dans quelques secondes.');
          setShowModal(false);
          resetForm();
          
          // Commencer le polling pour récupérer le nouveau résultat
          pollForResult(jobId);
        } else {
          alert('Votre feedback a été enregistré. Merci !');
          setShowModal(false);
          resetForm();
        }
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Une erreur est survenue lors de l\'envoi du feedback');
    } finally {
      setIsSubmitting(false);
      setIsRegenerating(false);
    }
  };

  const resetForm = () => {
    setFeedbackType('');
    setFeedbackText('');
  };

  const pollForResult = async (jobId: string, attempts: number = 0) => {
    const maxAttempts = 20; // Max 20 tentatives (environ 20 secondes)
    
    if (attempts >= maxAttempts) {
      alert('La régénération prend plus de temps que prévu. Veuillez rafraîchir la page.');
      return;
    }

    try {
      const token = authService.getToken();
      const response = await axios.get(
        `${config.api.titreEndpoint}/job/${jobId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      // L'API retourne les données directement
      const jobData = response.data;
      
      if (jobData.status === 'COMPLETED' && jobData.result) {
        // Nouveau résultat disponible !
        if (onRegenerated) {
          onRegenerated({
            title: jobData.result.titre,
            summary: jobData.result.resume
          });
        }
        alert('Le contenu a été régénéré avec succès !');
      } else if (jobData.status === 'FAILED') {
        alert('La régénération a échoué. Veuillez réessayer.');
      } else {
        // Toujours en cours, réessayer dans 1 seconde
        setTimeout(() => pollForResult(jobId, attempts + 1), 1000);
      }
    } catch (error) {
      console.error('Error polling for result:', error);
      // Réessayer en cas d'erreur temporaire
      setTimeout(() => pollForResult(jobId, attempts + 1), 1000);
    }
  };

  return (
    <>
      {/* Boutons de feedback */}
      <div className="flex items-center gap-2 mt-4 p-3 bg-gray-50 rounded-lg">
        <span className="text-sm text-gray-600">Ce résultat vous convient-il ?</span>
        <button
          onClick={handlePositiveFeedback}
          className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
          title="Parfait"
        >
          <ThumbsUp size={16} />
          <span className="text-sm">Parfait</span>
        </button>
        <button
          onClick={handleNegativeFeedback}
          className="flex items-center gap-1 px-3 py-1.5 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
          title="À améliorer"
        >
          <ThumbsDown size={16} />
          <span className="text-sm">À améliorer</span>
        </button>
      </div>

      {/* Modal de feedback détaillé */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Que souhaitez-vous améliorer ?</h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-3 mb-4">
              {feedbackOptions.map(option => (
                <label key={option.value} className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="feedbackType"
                    value={option.value}
                    checked={feedbackType === option.value}
                    onChange={(e) => setFeedbackType(e.target.value)}
                    className="mt-1"
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              ))}
            </div>

            <textarea
              placeholder="Commentaire optionnel (obligatoire si 'Autre problème' est sélectionné)..."
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              className="w-full p-2 border rounded-md resize-none h-20 text-sm"
              required={feedbackType === 'autre'}
            />

            <div className="flex gap-2 mt-4">
              <button
                onClick={() => submitFeedback('save_only')}
                disabled={isSubmitting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 transition-colors"
              >
                <Send size={16} />
                <span>Juste signaler</span>
              </button>
              <button
                onClick={() => submitFeedback('regenerate')}
                disabled={isSubmitting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:bg-gray-300 transition-colors"
              >
                {isRegenerating ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    <span>Régénération...</span>
                  </>
                ) : (
                  <>
                    <RefreshCw size={16} />
                    <span>Régénérer</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};